import type * as React from 'react'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { Input } from 'Components/_theme'

import { QUANTITY_INPUT } from '../OrderSummary.constants'
import type { OrderSummaryQuantityInputProps } from '../OrderSummary.types'

export const OrderSummaryQuantityInput = (
  props: OrderSummaryQuantityInputProps,
) => {
  const {
    controlId,
    defaultValue,
    minQuantity = 1,
    maxQuantity,
    onChangeInput,
    canChooseOnlyMinOrMaxQuantity,
  } = props
  const [quantity, setQuantity] = useState(defaultValue)

  const intl = useIntl()

  const handleChangeQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event
    const value = Number(target.value)

    if (
      !Number.isSafeInteger(value) ||
      value < minQuantity ||
      (maxQuantity !== undefined && value > maxQuantity)
    ) {
      return
    }

    let valueToSet = value

    // "0 or all" only makes sense when there is a max to jump to.
    if (canChooseOnlyMinOrMaxQuantity && maxQuantity !== undefined) {
      valueToSet = value > quantity ? maxQuantity : minQuantity
    }

    setQuantity(valueToSet)
    onChangeInput(valueToSet)
  }

  useEffect(() => {
    // Change the quantity if max quantity is lower. It happens when user changes
    // main device quantity but accessory quantity input is cached here, even when
    // max quantity becomes lower.
    if (maxQuantity !== undefined && maxQuantity < quantity) {
      setQuantity(maxQuantity)
    }

    if (
      canChooseOnlyMinOrMaxQuantity &&
      maxQuantity !== undefined &&
      quantity > 0 &&
      maxQuantity !== quantity
    ) {
      setQuantity(maxQuantity)
    }
  }, [canChooseOnlyMinOrMaxQuantity, maxQuantity, onChangeInput, quantity])

  return (
    <Input
      aria-label={intl.formatMessage({ id: 'NewOrderSummary_quantity' })}
      autoComplete="off"
      className="px-0 text-center w-50"
      data-testid={QUANTITY_INPUT}
      id={controlId}
      max={maxQuantity}
      min={minQuantity}
      onChange={handleChangeQuantity}
      required
      step={1}
      type="number"
      // toString prevents input number type leading zeros
      // makes it 1 instead of 00001 when user types "0000number" when minQuantitiy is 0
      value={quantity.toString()}
    />
  )
}
